//
//  config.tsx
//
//  isana.net,inc. Proprietary and Confidential
//

import { Route } from "react-router-dom";
import NotFound from "../NotFound";
import Login from "../Login";
import Terms from "../Terms";
import OSSLisence from "../OSSLisence";
import AdminMain from "../AdminMain";
import FitbitIntegration from "../FitbitIntegration";
import FitinsightUserList from "../FitinsightUserList";
import BloodPressureRegistration from "../BloodPressureRegistration";
import BloodPressureHistory from "../BloodPressureHistory";
import ImportUser from "../ImportUser";
import CsvExport from "../CsvExport";
import CsvDownloadList from "../CsvDownloadList";
import GoogleHealthAccountLinkage from "../googleHealth/GoogleHealthAccountLinkage";
import GoogleHealthAccountLinkageComplete from "../googleHealth/GoogleHealthAccountLinkageComplete";
import GhaCsvExport from "../GhaCsvExport";
import { PrivateRoute } from "../components/PrivateRoute";
import {
  BLOOD_PRESSURE_USED,
  CSV_EXPORT_USED,
  GOOGLE_HEALTH_CSV_EXPORT_ENABLED,
  TERMS_PAGE_TEXT,
} from "../utils/env";

// 画面用環境変数型
export interface RouteEnv {
  BLOOD_PRESSURE_USED: boolean;
  CSV_EXPORT_USED: boolean;
  GOOGLE_HEALTH_CSV_EXPORT_ENABLED: boolean;
  TERMS_PAGE_TEXT: string;
}

// ルートメタ情報の型定義
interface RouteMetadata {
  key: string;
  path: string;
  component: React.ComponentType;
  requiresAuth: boolean; // 認証が必要かどうか
  enableCondition?: () => boolean; // 有効化条件（環境変数など）
}

// 全ルートのメタ情報定義
const createRouteMetadata = (env: RouteEnv): RouteMetadata[] => [
  // 公開ルート
  { key: "000", path: "*", component: NotFound, requiresAuth: false },
  {
    key: "007",
    path: "/google-health-account-linkage",
    component: GoogleHealthAccountLinkage,
    requiresAuth: false,
  },
  {
    key: "008",
    path: "/google-health-account-linkage-complete",
    component: GoogleHealthAccountLinkageComplete,
    requiresAuth: false,
  },
  { key: "003", path: "/Login", component: Login, requiresAuth: false },
  {
    key: "005",
    path: "/terms",
    component: env.TERMS_PAGE_TEXT.length === 0 ? NotFound : Terms,
    requiresAuth: false,
  },
  {
    key: "006",
    path: "/OSSLicense",
    component: OSSLisence,
    requiresAuth: false,
  },

  // 認証ルート
  { key: "100", path: "/AdminMain", component: AdminMain, requiresAuth: true },
  {
    key: "101",
    path: "/FitbitIntegration",
    component: FitbitIntegration,
    requiresAuth: true,
  },
  {
    key: "102",
    path: "/FitinsightUserList",
    component: FitinsightUserList,
    requiresAuth: true,
  },
  {
    key: "104",
    path: "/BloodPressureRegistration",
    component: BloodPressureRegistration,
    requiresAuth: true,
    enableCondition: () => env.BLOOD_PRESSURE_USED,
  },
  {
    key: "105",
    path: "/BloodPressureHistory",
    component: BloodPressureHistory,
    requiresAuth: true,
    enableCondition: () => env.BLOOD_PRESSURE_USED,
  },
  {
    key: "106",
    path: "/ImportUser",
    component: ImportUser,
    requiresAuth: true,
    enableCondition: () => env.BLOOD_PRESSURE_USED,
  },
  {
    key: "107",
    path: "/CsvExport",
    component: CsvExport,
    requiresAuth: true,
    enableCondition: () => env.CSV_EXPORT_USED,
  },
  {
    key: "108",
    path: "/CsvDownloadList",
    component: CsvDownloadList,
    requiresAuth: true,
    enableCondition: () => env.CSV_EXPORT_USED,
  },
  {
    key: "109",
    path: "/GhaCsvExport",
    component: GhaCsvExport,
    requiresAuth: true,
    enableCondition: () => env.GOOGLE_HEALTH_CSV_EXPORT_ENABLED,
  },
];

// デフォルトの画面用環境変数
const defaultRouteEnv: RouteEnv = {
  BLOOD_PRESSURE_USED,
  CSV_EXPORT_USED,
  GOOGLE_HEALTH_CSV_EXPORT_ENABLED,
  TERMS_PAGE_TEXT,
};

// 有効ルートを取得
const getEnabledRoutes = (env: RouteEnv = defaultRouteEnv) =>
  createRouteMetadata(env).filter(
    (meta) => !meta.enableCondition || meta.enableCondition()
  );

// 公開ルートを取得
export const getPublicRoutes = (env: RouteEnv = defaultRouteEnv) =>
  getEnabledRoutes(env).filter((meta) => !meta.requiresAuth);

// 認証ルートを取得
export const getPrivateRoutes = (env: RouteEnv = defaultRouteEnv) =>
  getEnabledRoutes(env).filter((meta) => meta.requiresAuth);

// メタ情報からルートを生成
export const createAllRoutes = (
  env: RouteEnv = defaultRouteEnv
): React.ReactElement[] => {
  // 認証不要ルート
  const publicRoutes = getPublicRoutes(env).map((meta) => {
    const Component = meta.component;
    return <Route key={meta.key} path={meta.path} element={<Component />} />;
  });

  // 認証必要ルート
  const privateRoutes = getPrivateRoutes(env).map((meta) => {
    const Component = meta.component;
    return (
      <Route
        key={meta.key}
        path={meta.path}
        element={
          <PrivateRoute>
            <Component />
          </PrivateRoute>
        }
      />
    );
  });

  return [...publicRoutes, ...privateRoutes];
};
